/*
 * Interactive Penta widget for the knowledge base (penta-group-dynamics page).
 *
 * Hydrates every `figure.hd-penta-widget` (whose server-rendered SVG from
 * docs_diagrams.penta_svg is the no-JS/SEO fallback) into a small lab: a cast
 * of five fictional members the reader toggles on and off, with the strip
 * re-lighting instantly. It demonstrates the three ideas that make Penta
 * click: any member fills a gate for the whole group, a gap is structural
 * (remove one person and a function vanishes), and the right addition closes
 * a gap for everyone.
 *
 * Geometry, class names and encodings mirror the dashboard strip
 * (penta-strip.js PentaStrip.GEOM/svg + group.css .pn-*); styles in penta.css.
 * Vanilla JS, no dependencies (mandala.js is the precedent).
 */
(function () {
  "use strict";

  // This page's language, from /docs/i18n/<lang>.js (written by build_docs). The
  // centers, channel names and gate keynotes below are the product's own penta.*
  // strings, already translated for the group page — the same six channels should
  // not be named twice in one product.
  const T =
    window.GHD_DOCS_T ||
    function (key, fallback, vars) {
      return vars ? String(fallback).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : vars[k])) : fallback;
    };

  // --- The strip (mirrors PentaStrip.GEOM / PentaStrip.CHANNELS) ----------
  const GEOM = {
    columnX: [96, 190, 284],
    bars: [
      {
        y: 30,
        label: T("penta.center_throat", "Throat"),
        role: T("penta.center_throat_role", "how the group speaks & presents"),
      },
      { y: 213, label: T("penta.center_g", "G"), role: T("penta.center_g_role", "where the group is headed") },
      {
        y: 396,
        label: T("penta.center_sacral", "Sacral"),
        role: T("penta.center_sacral_role", "the energy that sustains it"),
      },
    ],
    upper: { lineY1: 101, lineY2: 186, topCy: 88, bottomCy: 199, nameY: 147 },
    lower: { lineY1: 284, lineY2: 369, topCy: 271, bottomCy: 382, nameY: 330 },
  };

  // Canonical drawing order; top = G-side gate below, Throat-side gate above.
  // Channel names stay English in every language (the glossary in i18n/README.md
  // — practitioners worldwide use them), which is why these read from penta.* with
  // the English name as the fallback rather than being translated here.
  const CHANNELS = [
    { id: "5-15", name: T("penta.channel.5-15", "Rhythm"), top: 15, bottom: 5, band: "lower" },
    { id: "2-14", name: T("penta.channel.2-14", "The Beat"), top: 2, bottom: 14, band: "lower" },
    { id: "29-46", name: T("penta.channel.29-46", "Discovery"), top: 46, bottom: 29, band: "lower" },
    { id: "7-31", name: T("penta.channel.7-31", "The Alpha"), top: 31, bottom: 7, band: "upper" },
    { id: "1-8", name: T("penta.channel.1-8", "Inspiration"), top: 8, bottom: 1, band: "upper" },
    { id: "13-33", name: T("penta.channel.13-33", "The Prodigal"), top: 33, bottom: 13, band: "upper" },
  ];

  // Single keynote set — matches the product (i18n penta.gate.<n>).
  const GATE_KEYNOTES = {};
  [
    [1, "Implementation"],
    [2, "Vision"],
    [5, "Culture"],
    [7, "Planning"],
    [8, "Public Relations"],
    [13, "Accounting"],
    [14, "Capacity"],
    [15, "Reliability"],
    [29, "Commitment"],
    [31, "Administration"],
    [33, "Oversight"],
    [46, "Coordination"],
  ].forEach(function (pair) {
    GATE_KEYNOTES[pair[0]] = T("penta.gate." + pair[0], pair[1]);
  });

  // --- The cast (fictional; gates picked so the scenarios demonstrate
  // define / gap / over-defined transitions — verify against CHANNELS) ------
  //
  // The five names are deliberately NOT translated, only the roles are. The
  // article beside this widget walks the reader through these people by name
  // ("Switch Sam off…", "Ava's gate 8…"), so the prose has to match the buttons
  // — and all seven docs/knowledge-base/*/relationships/penta-group-dynamics.md
  // quote them verbatim for that reason. They are neutral international first
  // names, which
  // is what makes that affordable. Changing one here silently desynchronises
  // seven pages from the widget they describe.
  //
  // To localize them after all, all three of these move together:
  //   1. the names become docs-catalog keys (i18n/<locale>/docs.json), read
  //      through T() like the roles below;
  //   2. the seven penta-group-dynamics.md pages are rewritten to the new names;
  //   3. pw.preset.hire.label ("Hire Ava") splits — it bakes a name into a
  //      translated string, so it has to become label + name, not one phrase.
  const CAST = [
    { id: "maya", name: "Maya", role: T("pw.role.founder", "founder"), slot: "m1", gates: [2, 14, 31] },
    { id: "leo", name: "Leo", role: T("pw.role.engineer", "engineer"), slot: "m2", gates: [1, 5, 14, 29] },
    { id: "sam", name: "Sam", role: T("pw.role.operations", "operations"), slot: "m3", gates: [7, 15, 46] },
    { id: "ava", name: "Ava", role: T("pw.role.marketing", "marketing"), slot: "m4", gates: [8, 33] },
    { id: "noor", name: "Noor", role: T("pw.role.finance", "finance"), slot: "m5", gates: [13, 15] },
  ];

  const PRESETS = [
    {
      id: "trio",
      label: T("pw.preset.trio.label", "Founding trio"),
      members: ["maya", "leo", "sam"],
      story: T("pw.preset.trio.story", ""),
    },
    {
      id: "hire",
      label: T("pw.preset.hire.label", "Hire Ava"),
      members: ["maya", "leo", "sam", "ava"],
      story: T("pw.preset.hire.story", ""),
    },
    {
      id: "five",
      label: T("pw.preset.five.label", "All five"),
      members: ["maya", "leo", "sam", "ava", "noor"],
      story: T("pw.preset.five.story", ""),
    },
  ];

  // --- Rendering -----------------------------------------------------------

  function gatePositions() {
    const pos = {};
    let ui = 0;
    let li = 0;
    CHANNELS.forEach((ch) => {
      const band = ch.band === "upper" ? GEOM.upper : GEOM.lower;
      const x = GEOM.columnX[ch.band === "upper" ? ui++ : li++];
      pos[ch.top] = { x, cy: band.topCy };
      pos[ch.bottom] = { x, cy: band.bottomCy };
      ch.x = x;
      ch.geom = band;
    });
    return pos;
  }

  const GATE_POS = gatePositions();

  function stripSkeleton() {
    const bars = GEOM.bars
      .map(
        (bar) =>
          `<rect class="pn-center-bar" x="46" y="${bar.y}" width="288" height="44" rx="10"/>` +
          `<text class="pn-center-label" x="190" y="${bar.y + 19}" text-anchor="middle">${bar.label}</text>` +
          `<text class="pn-center-role" x="190" y="${bar.y + 33}" text-anchor="middle">${bar.role}</text>`,
      )
      .join("");

    const columns = CHANNELS.map((ch) => {
      const star =
        ch.id === "7-31"
          ? `<path class="pn-alpha-star" d="M ${ch.x} ${ch.geom.topCy + 18} ` +
            `l 2.2 4.4 4.9 0.7 -3.5 3.4 0.8 4.9 -4.4 -2.3 -4.4 2.3 0.8 -4.9 -3.5 -3.4 4.9 -0.7 Z"/>`
          : "";
      const gate = (g, cy) =>
        `<g class="pn-gate-hit" data-gate="${g}"><title></title>` +
        `<circle class="pn-gate off" cx="${ch.x}" cy="${cy}" r="14"/>` +
        `<text class="pn-gate-num off" x="${ch.x}" y="${cy}">${g}</text></g>` +
        `<g class="pn-dots" data-gate="${g}" aria-hidden="true"></g>`;
      return (
        `<line class="pn-chan-line" data-channel="${ch.id}" x1="${ch.x}" y1="${ch.geom.lineY1}" x2="${ch.x}" y2="${ch.geom.lineY2}"/>` +
        `<g class="pn-chan-hit" data-channel="${ch.id}"><title></title>` +
        `<text class="pn-chan-name" data-channel="${ch.id}" x="${ch.x}" y="${ch.geom.nameY}">${ch.name}</text></g>` +
        star +
        gate(ch.top, ch.geom.topCy) +
        gate(ch.bottom, ch.geom.bottomCy)
      );
    }).join("");

    return (
      '<svg class="pn-strip" viewBox="0 0 380 470" role="img" aria-label="" xmlns="http://www.w3.org/2000/svg">' +
      "<defs>" +
      '<marker id="pnDocsArrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">' +
      '<path class="pn-arrowhead" d="M0,0 L7,3.5 L0,7 Z"/></marker>' +
      "</defs>" +
      '<path class="pn-flow-arrow" d="M 24 380 L 24 292"/>' +
      '<path class="pn-flow-arrow" d="M 24 197 L 24 109"/>' +
      '<text class="pn-flow-label" x="13" y="336" text-anchor="middle" transform="rotate(-90 13 336)">fuel</text>' +
      '<text class="pn-flow-label" x="13" y="153" text-anchor="middle" transform="rotate(-90 13 153)">voice</text>' +
      bars +
      columns +
      "</svg>"
    );
  }

  function memberButton(m) {
    return (
      `<button type="button" class="pw-member" data-member="${m.id}" aria-pressed="false" ` +
      `title="${m.name} carries gates ${m.gates.join(", ")}">` +
      `<span class="pw-dot ${m.slot}" aria-hidden="true">${m.name[0]}</span>` +
      `<span class="pw-who"><span class="pw-name">${m.name}</span>` +
      `<span class="pw-role">${m.role}</span></span></button>`
    );
  }

  function hydrate(figure) {
    if (figure.classList.contains("is-live")) return;
    const caption = figure.querySelector("figcaption");
    figure.classList.add("is-live");
    figure.innerHTML =
      '<div class="pw-presets" role="group" aria-label="' +
      T("pw.presets_aria", "Example scenarios") +
      '">' +
      '<span class="pw-presets-label">' +
      T("pw.presets_label", "Try") +
      "</span>" +
      PRESETS.map(
        (p) => `<button type="button" class="pw-preset" data-preset="${p.id}" aria-pressed="false">${p.label}</button>`,
      ).join("") +
      "</div>" +
      '<div class="pw-body">' +
      '<div class="pw-members" role="group" aria-label="' +
      T("pw.members_aria", "Group members — toggle to add or remove") +
      '">' +
      CAST.map(memberButton).join("") +
      '<p class="pw-hint">' +
      T("pw.hint", "Tap a person to add or remove them — the strip re-lights instantly.") +
      "</p>" +
      "</div>" +
      `<div class="pw-strip-wrap">${stripSkeleton()}</div>` +
      "</div>" +
      '<div class="pw-status"><p class="pw-story"></p><p class="pw-meta" aria-live="polite"></p></div>';
    if (caption) figure.appendChild(caption);

    const els = {
      svg: figure.querySelector(".pn-strip"),
      story: figure.querySelector(".pw-story"),
      meta: figure.querySelector(".pw-meta"),
      line: {},
      name: {},
      chanTitle: {},
      gateCircle: {},
      gateNum: {},
      gateTitle: {},
      dots: {},
      memberBtns: {},
      presetBtns: {},
    };
    CHANNELS.forEach((ch) => {
      els.line[ch.id] = figure.querySelector(`.pn-chan-line[data-channel="${ch.id}"]`);
      els.name[ch.id] = figure.querySelector(`.pn-chan-name[data-channel="${ch.id}"]`);
      els.chanTitle[ch.id] = figure.querySelector(`.pn-chan-hit[data-channel="${ch.id}"] title`);
    });
    Object.keys(GATE_POS).forEach((g) => {
      els.gateCircle[g] = figure.querySelector(`.pn-gate-hit[data-gate="${g}"] .pn-gate`);
      els.gateNum[g] = figure.querySelector(`.pn-gate-hit[data-gate="${g}"] .pn-gate-num`);
      els.gateTitle[g] = figure.querySelector(`.pn-gate-hit[data-gate="${g}"] title`);
      els.dots[g] = figure.querySelector(`.pn-dots[data-gate="${g}"]`);
    });
    CAST.forEach((m) => {
      els.memberBtns[m.id] = figure.querySelector(`.pw-member[data-member="${m.id}"]`);
    });
    PRESETS.forEach((p) => {
      els.presetBtns[p.id] = figure.querySelector(`.pw-preset[data-preset="${p.id}"]`);
    });

    const active = new Set(PRESETS[0].members);

    function activePreset() {
      return PRESETS.find((p) => p.members.length === active.size && p.members.every((id) => active.has(id))) || null;
    }

    function carriersOf(gate) {
      return CAST.filter((m) => active.has(m.id) && m.gates.indexOf(Number(gate)) !== -1);
    }

    function renderDots(gate) {
      const carriers = carriersOf(gate);
      const { x, cy } = GATE_POS[gate];
      const n = carriers.length;
      const spacing = n <= 2 ? 18 : 13;
      const startY = cy - ((n - 1) * spacing) / 2;
      els.dots[gate].innerHTML = carriers
        .map((m, i) => {
          const dotCy = startY + i * spacing;
          return (
            `<g class="mdot ${m.slot}"><circle class="${m.slot}" cx="${x + 22}" cy="${dotCy}" r="9"/>` +
            `<text class="${m.slot}" x="${x + 22}" y="${dotCy}">${m.name[0]}</text></g>`
          );
        })
        .join("");
    }

    function update() {
      const defined = CHANNELS.filter((ch) => carriersOf(ch.top).length && carriersOf(ch.bottom).length);
      const definedIds = new Set(defined.map((ch) => ch.id));
      const gaps = CHANNELS.filter((ch) => !definedIds.has(ch.id));
      const doubled = Object.keys(GATE_POS)
        .map(Number)
        .filter((g) => carriersOf(g).length > 1)
        .sort((a, b) => a - b);

      CHANNELS.forEach((ch) => {
        const on = definedIds.has(ch.id);
        els.line[ch.id].classList.toggle("on", on);
        els.name[ch.id].classList.toggle("on", on);
        els.chanTitle[ch.id].textContent = `${ch.name} (${ch.id}) — ${on ? "defined" : "open (a gap)"}`;
      });
      Object.keys(GATE_POS).forEach((g) => {
        const carriers = carriersOf(g);
        els.gateCircle[g].classList.toggle("off", !carriers.length);
        els.gateNum[g].classList.toggle("off", !carriers.length);
        els.gateTitle[g].textContent = carriers.length
          ? `Gate ${g} · ${GATE_KEYNOTES[g]} — carried by ${carriers.map((m) => m.name).join(", ")}`
          : `Gate ${g} · ${GATE_KEYNOTES[g]} — no one carries it`;
        renderDots(g);
      });

      CAST.forEach((m) => {
        els.memberBtns[m.id].setAttribute("aria-pressed", active.has(m.id) ? "true" : "false");
      });
      const preset = activePreset();
      PRESETS.forEach((p) => {
        els.presetBtns[p.id].setAttribute("aria-pressed", preset && preset.id === p.id ? "true" : "false");
      });

      figure.classList.toggle("is-forming", active.size < 3);
      els.svg.setAttribute(
        "aria-label",
        T("penta.aria_strip", "Penta diagram: {defined} of 6 channels defined", { defined: defined.length }) +
          (gaps.length
            ? T("pw.aria_gaps", "; gaps: {list}", {
                list: gaps.map((ch) => `${ch.name} (${ch.id})`).join(", "),
              })
            : ""),
      );

      // Story + live meta line.
      if (active.size < 3) {
        els.story.textContent = T("pw.story_too_few", "");
      } else if (preset) {
        els.story.textContent = preset.story;
      } else if (defined.length === 6) {
        els.story.textContent = T("pw.story_complete", "");
      } else {
        els.story.textContent = T("pw.story_partial", "", { defined: defined.length });
      }

      let meta = T("pw.meta_defined", "<strong>{defined} of 6</strong> channels defined", {
        defined: defined.length,
      });
      if (gaps.length) {
        meta +=
          " · " +
          T("pw.meta_open", "open:") +
          " " +
          gaps
            .map(
              (ch) =>
                `<span class="pw-chip pw-chip-gap" title="${GATE_KEYNOTES[ch.bottom]} · ${GATE_KEYNOTES[ch.top]}">` +
                `${ch.name} ${ch.id}</span>`,
            )
            .join(" ");
      }
      if (doubled.length) {
        meta +=
          " · " +
          T("pw.meta_doubled", "doubled:") +
          " " +
          doubled
            .map(
              (g) =>
                `<span class="pw-chip pw-chip-dup">${T("pw.meta_gate", "gate {gate}", { gate: g })} · ${GATE_KEYNOTES[g]} ` +
                `(${carriersOf(g)
                  .map((m) => m.name)
                  .join(" + ")})</span>`,
            )
            .join(" ");
      }
      els.meta.innerHTML = meta;
    }

    figure.querySelectorAll(".pw-member").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-member");
        if (active.has(id)) active.delete(id);
        else active.add(id);
        update();
      });
    });
    figure.querySelectorAll(".pw-preset").forEach((btn) => {
      btn.addEventListener("click", () => {
        const preset = PRESETS.find((p) => p.id === btn.getAttribute("data-preset"));
        active.clear();
        preset.members.forEach((id) => active.add(id));
        update();
      });
    });

    update();
  }

  // `root` is the in-app panel's article (kb-panel.js); a doc page boots the
  // whole document.
  function boot(root) {
    (root || document).querySelectorAll("figure.hd-penta-widget").forEach(hydrate);
  }

  window.GHD_PENTA = { boot: boot };
  boot();
})();
